// MobileAttendanceA — Variation A · Vibrant mobile Attendance home.
// Rendered by pages/attendance/AttendanceDashboard.js when viewport <768px
// (all roles — the same screen serves employees and admins; admin-only stats
// row is hidden when role !== admin/super_admin).
//
// Design source: public/design/dnexus-mobile-crm.html  (lines 666-760)

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MobileBottomTabBar from "./MobileBottomTabBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function nowClock() {
  return new Date().toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

export default function MobileAttendanceA({ userRole = "employee" }) {
  const navigate = useNavigate();
  const isAdmin = userRole === "admin" || userRole === "super_admin";

  const [todayStatus, setTodayStatus] = useState(null);
  const [stats, setStats] = useState({ total: 0, present: 0, absent: 0, late: 0 });
  const [punching, setPunching] = useState(false);
  const [clock, setClock] = useState(nowClock());

  // Live clock for "Punch In Now" subtext
  useEffect(() => {
    const id = setInterval(() => setClock(nowClock()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const loadToday = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/attendance/today`, { credentials: "include" });
      if (r.ok) setTodayStatus(await r.json());
    } catch (_) {}
  }, []);

  const loadAdminStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      // Staff total
      const sRes = await fetch(`${BACKEND_URL}/api/attendance/staff`, { credentials: "include" });
      const staff = sRes.ok ? await sRes.json() : [];
      const total = Array.isArray(staff) ? staff.length : (staff?.staff?.length || staff?.length || 0);

      // Daily report (present / absent / late counts)
      const dRes = await fetch(`${BACKEND_URL}/api/attendance/reports/daily?date=${todayISO()}`, { credentials: "include" });
      let present = 0, absent = 0, late = 0;
      if (dRes.ok) {
        const data = await dRes.json();
        // The endpoint shape varies — try a few common shapes
        const records = data?.records || data?.data || (Array.isArray(data) ? data : []);
        for (const rec of records) {
          const st = String(rec.status || rec.attendance_status || "").toLowerCase();
          if (st === "present" || rec.punch_in_time) present++;
          else if (st === "absent") absent++;
          if (rec.is_late || rec.late_minutes > 0) late++;
        }
        // Fall back to summary fields if the endpoint provides them
        if (data?.summary) {
          if (typeof data.summary.present === "number") present = data.summary.present;
          if (typeof data.summary.absent === "number") absent = data.summary.absent;
          if (typeof data.summary.late === "number") late = data.summary.late;
        }
      }
      setStats({ total, present, absent, late });
    } catch (_) {}
  }, [isAdmin]);

  useEffect(() => { loadToday(); loadAdminStats(); }, [loadToday, loadAdminStats]);

  const punchedIn = !!todayStatus?.record?.punch_in_time;
  const punchedOut = !!todayStatus?.record?.punch_out_time;
  const statusLabel = punchedOut ? "Punched Out" : punchedIn ? "On the Clock" : "Not Punched In";

  const handlePunchIn = async () => {
    if (punching) return;
    if (punchedIn && !punchedOut) {
      // Already punched in — full punch-in/out flow lives on /attendance/punch.
      navigate("/attendance/punch");
      return;
    }
    setPunching(true);
    try {
      // Geo not required for the headline action; the dedicated /attendance/punch page collects location.
      const r = await fetch(`${BACKEND_URL}/api/attendance/punch-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (r.ok) {
        toast.success("Punched in");
        loadToday();
      } else {
        // Most likely cause is location required — push to the full punch page
        toast.message("Open punch page");
        navigate("/attendance/punch");
      }
    } catch (_) {
      navigate("/attendance/punch");
    } finally {
      setPunching(false);
    }
  };

  const dateLabel = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div
      className="bg-[#F6F6FB] flex flex-col"
      style={{
        margin: "-1rem -1rem 0 -1rem",
        minHeight: "calc(100vh - 4rem)",
        fontFamily: FONT,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
      }}
      data-testid="mobile-attendance-a"
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 6px" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1B1F2A", letterSpacing: "-.5px" }}>Attendance</div>
          <div style={{ fontSize: 12, color: "#8A8F9C", fontWeight: 600 }}>Today · {dateLabel}</div>
        </div>
        <button
          onClick={() => navigate("/attendance/punch")}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: punchedIn && !punchedOut ? "linear-gradient(135deg,#FB7185,#F43F5E)" : "linear-gradient(135deg,#34D399,#059669)",
            color: "#fff", fontSize: 12.5, fontWeight: 800, padding: "10px 14px", borderRadius: 12,
            boxShadow: "0 8px 16px rgba(5,150,105,.3)", border: "none",
          }}
          data-testid="mobile-attendance-punch-pill"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="1.8"/><path d="M12 7.5V12l3 2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>
          {punchedIn && !punchedOut ? "Punch Out" : "Punch In"}
        </button>
      </div>

      <div style={{ flex: 1, padding: "6px 16px 14px" }}>
        {/* Status hero */}
        <div
          style={{
            borderRadius: 20, padding: 18, color: "#fff",
            background: punchedIn && !punchedOut
              ? "linear-gradient(135deg,#34D399,#059669)"
              : punchedOut
                ? "linear-gradient(135deg,#A3A3A3,#525252)"
                : "linear-gradient(135deg,#10B981,#059669)",
            boxShadow: "0 14px 26px rgba(5,150,105,.28)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, opacity: .92 }}>My Status Today</div>
          <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, letterSpacing: "-.4px" }}>{statusLabel}</div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, gap: 12 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, opacity: .95 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="1.7"/><path d="M12 7.5V12l3 2" stroke="#fff" strokeWidth="1.7" strokeLinecap="round"/></svg>
              {punchedIn ? `In · ${fmtTime(todayStatus?.record?.punch_in_time)}` : clock}
              {punchedOut && <> · Out · {fmtTime(todayStatus?.record?.punch_out_time)}</>}
            </span>
            <button
              onClick={handlePunchIn}
              disabled={punching}
              style={{
                background: "#fff",
                color: punchedIn && !punchedOut ? "#E11D48" : "#059669",
                fontSize: 12.5, fontWeight: 800, padding: "9px 16px", borderRadius: 11, border: "none",
                opacity: punching ? .6 : 1,
              }}
              data-testid="mobile-attendance-punch-cta"
            >
              {punching ? "Saving…" : (punchedIn && !punchedOut ? "Punch Out" : punchedOut ? "Day Closed" : "Punch In Now")}
            </button>
          </div>
        </div>

        {/* Admin stat grid (admin / super_admin only) */}
        {isAdmin && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginTop: 14 }}>
            <StatTile color="#1B1F2A" iconBg="#EEF0FF" label="Total Staff" value={stats.total} iconStroke="#6366F1"
              iconPath={<><circle cx="9" cy="8" r="3" stroke="#6366F1" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round"/><circle cx="17" cy="9" r="2.2" stroke="#6366F1" strokeWidth="1.8"/></>} />
            <StatTile color="#10B981" iconBg="#E7F8F0" label="Present" value={stats.present}
              iconPath={<><circle cx="9" cy="8" r="3" stroke="#10B981" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5 1.2 0 2.3.3 3.2.9" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round"/><path d="M15 13l2 2 4-4" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></>} />
            <StatTile color="#F43F5E" iconBg="#FFF1F2" label="Absent" value={stats.absent}
              iconPath={<><circle cx="9" cy="8" r="3" stroke="#F43F5E" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5 1.2 0 2.3.3 3.2.9" stroke="#F43F5E" strokeWidth="1.8" strokeLinecap="round"/><path d="M16 12l5 5M21 12l-5 5" stroke="#F43F5E" strokeWidth="1.8" strokeLinecap="round"/></>} />
            <StatTile color="#F59E0B" iconBg="#FEF4E6" label="Late Today" value={stats.late}
              iconPath={<><path d="M12 3l9.5 16.5H2.5L12 3z" stroke="#F59E0B" strokeWidth="1.8" strokeLinejoin="round"/><path d="M12 10v3.5M12 16.5h.01" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round"/></>} />
          </div>
        )}

        {/* This Month */}
        <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 20, padding: "6px 14px", marginTop: 14, boxShadow: "0 1px 3px rgba(17,17,40,.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "13px 0 11px", borderBottom: "1px solid #F2F2F6" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3.5" y="5" width="17" height="15" rx="3" stroke="#6366F1" strokeWidth="1.8"/><path d="M3.5 9.5h17M8 3v4M16 3v4" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round"/></svg>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A" }}>This Month</span>
          </div>
          <MonthRow iconBg="#FEF4E6" icon={<><path d="M12 3l9.5 16.5H2.5L12 3z" stroke="#F59E0B" strokeWidth="1.8" strokeLinejoin="round"/><path d="M12 10v3.5M12 16.5h.01" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round"/></>} label="Total Late Entries" value={todayStatus?.month?.late_entries ?? 0} />
          <MonthRow iconBg="#E7F8F0" icon={<><path d="M3 17l5-5 4 3 6-8" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 7h5v5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></>} label="Total Overtime Hours" value={`${todayStatus?.month?.overtime_hours ?? 0}h`} last={false} />
          <MonthRow iconBg="#EEF0FF" icon={<><rect x="3" y="6" width="18" height="13" rx="3" stroke="#6366F1" strokeWidth="1.8"/><path d="M3 10h18" stroke="#6366F1" strokeWidth="1.8"/></>} label="Pending Salary Slips" value={todayStatus?.month?.pending_slips ?? 0} last />
        </div>

        {/* Quick Actions */}
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A", marginTop: 16, marginBottom: 10 }}>Quick Actions</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <ActionRow onClick={() => navigate("/attendance/records")} label="View Attendance Records" iconBg="#EEF0FF"
            icon={<><rect x="5" y="3" width="14" height="18" rx="2.5" stroke="#6366F1" strokeWidth="1.8"/><path d="M9 8h6M9 12h6M9 16h3" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round"/></>} />
          {isAdmin && (
            <ActionRow onClick={() => navigate("/attendance/staff")} label="Manage Staff Profiles" iconBg="#E7F8F0"
              icon={<><circle cx="9" cy="8" r="3" stroke="#10B981" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round"/><circle cx="17" cy="9" r="2.2" stroke="#10B981" strokeWidth="1.8"/></>} />
          )}
          <ActionRow onClick={() => navigate("/attendance/salary")} label="Salary Management" iconBg="#F3EDFE"
            icon={<><rect x="3" y="6" width="18" height="13" rx="3" stroke="#8B5CF6" strokeWidth="1.8"/><path d="M3 10h18" stroke="#8B5CF6" strokeWidth="1.8"/></>} />
          {isAdmin && (
            <ActionRow onClick={() => navigate("/attendance/reports")} label="Generate Reports" iconBg="#FEF4E6"
              icon={<path d="M5 19V5M5 19h15M9 15v-4M13 15V8M17 15v-6" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>} />
          )}
          <ActionRow onClick={() => navigate("/attendance/leave")} label="Leave Management" iconBg="#FFF1F2"
            icon={<><path d="M5 4h14v17l-7-3-7 3V4z" stroke="#F43F5E" strokeWidth="1.8" strokeLinejoin="round"/></>} />
        </div>
      </div>

      <MobileBottomTabBar active="more" />
    </div>
  );
}

function StatTile({ color, iconBg, label, value, iconPath }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 13, display: "flex", alignItems: "center", gap: 11 }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">{iconPath}</svg>
      </div>
      <div>
        <div style={{ fontSize: 21, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 10.5, color: "#9AA0AE", fontWeight: 700, marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

function MonthRow({ iconBg, icon, label, value, last }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 0", borderBottom: last ? "none" : "1px solid #F2F2F6" }}>
      <div style={{ width: 30, height: 30, borderRadius: 9, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">{icon}</svg>
      </div>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#4B4F5C" }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 800, color: "#1B1F2A" }}>{value}</span>
    </div>
  );
}

function ActionRow({ onClick, label, iconBg, icon }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 11, background: "#fff", border: "1px solid #EFEFF5", borderRadius: 14, padding: 13, width: "100%", textAlign: "left", fontFamily: FONT, cursor: "pointer" }}>
      <div style={{ width: 30, height: 30, borderRadius: 9, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">{icon}</svg>
      </div>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#1B1F2A" }}>{label}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="#C4C8D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </button>
  );
}
